import {useState, useEffect} from 'react';

import * as store from '@/store';
import {IAiChatPanel} from '@/interface';
import {EDITOR_MODE} from '@/Constant';
import {useSyncExpand, useInitExpandData, useResetStoreData} from './hooks/useSyncExpand';
import {
    AIChatPanelExpandSource, aiChatPanelExpandSourceObj, setOpenAIChatPanelFlag,
} from '@/utils/document/aigc';
import {reportShowContentCount} from '@/buried/aigc';
import {reportShowMaterial} from '@/buried/material';
import {getMorphoService, serviceNames} from '@/rte/editor';
import {SideTabKey} from '@/interface';
import {RightSidebarWidth} from '@/constants/document/doc';

import Header from './Header';
import Entry from './Entry';
import CommentBg from './CommentBg';
import {ContinueChatData} from './interface';

enum RenderTarget {
    AiChat,
    AiAssistant,
}

let startRenderTarget = false;

interface IProps {
    curDocument: WizDocument;
    aiChatPanel?: IAiChatPanel;
    documentMode?: EDITOR_MODE;
    docReady?: boolean;
    renderTarget?: RenderTarget | null;
    continueChatData: ContinueChatData | null;
}

const Panel: React.FC<IProps> = ({
    curDocument,
    aiChatPanel,
    documentMode,
    docReady = false,
    renderTarget = null,
}) => {
    const {docGuid} = curDocument || {};
    const {isExpanded = false, selectedTab, showCommentBg} = aiChatPanel || {};

    // 切换文档时，重置source并初始化，应最先执行
    useEffect(() => {
        aiChatPanelExpandSourceObj.reset();
        aiChatPanelExpandSourceObj.init();
    }, [docGuid]);

    useEffect(() => {
        function cb() {
            setOpenAIChatPanelFlag(AIChatPanelExpandSource.refresh);
        }
        window.addEventListener('unload', cb);

        return () => {
            window.removeEventListener('unload', cb);
        };
    }, []);

    const renderTargetNotNull = renderTarget !== null;
    const isRenderAiAssistant = renderTarget === RenderTarget.AiAssistant;
    const showAi = isExpanded && selectedTab === SideTabKey.Ai;
    const showMaterial = isExpanded && selectedTab === SideTabKey.Material;
    startRenderTarget = startRenderTarget || (docReady && showAi);

    // 打开洞察或模板库，埋点上报
    useEffect(() => {
        if (docGuid && showAi) {
            reportShowContentCount({
                pageId: docGuid,
                panelExpandSource: aiChatPanelExpandSourceObj.getVal(),
            });
        } else if (docGuid && showMaterial) {
            reportShowMaterial({docId: docGuid});
        }
    }, [docGuid, showAi, showMaterial]);

    return docGuid === aiChatPanel?.docGuid && renderTargetNotNull ? (
        <>
            <Entry
                show={!isExpanded}
                documentMode={documentMode}
            />
            <Header
                show={isExpanded}
                isAiAssistant={isRenderAiAssistant}
                docGuid={docGuid}
            />

            {/* 划线评论兜底假背景，优化切换文档&模式时评论闪动问题 */}
            <CommentBg
                show={isExpanded && selectedTab === SideTabKey.Comment && !!showCommentBg}
                isAiAssistant={isRenderAiAssistant}
            />
        </>
    ) : null;
};

const PanelWrap = store.connect([
    store.KEYS.AI_CHAT_PANEL,
    store.KEYS.DOCUMENT_MODE,
    store.KEYS.DOC_READY,
])(Panel);

const AIChat: React.FC<{
    curDocument: WizDocument;
    continueChatData: ContinueChatData | null;
    aiChatPanel?: IAiChatPanel;
    documentMode?: EDITOR_MODE;
}> = ({
    curDocument,
    continueChatData,
    aiChatPanel,
    documentMode,
}) => {
    const showChat = true;
    const showAiPanel = store.getSDKShowAIPanel();
    // 初始化Store数据
    useInitExpandData({curDocument, aiChatPanel, documentMode});

    // 同步划线评论和AI的展开状态
    useSyncExpand({curDocument, aiChatPanel});

    // 组件销毁后重置store状态
    useResetStoreData();

    const [renderTarget, setRenderTarget] = useState<RenderTarget | null>(null);
    useEffect(() => {
        showAiPanel && setRenderTarget(RenderTarget.AiAssistant);
    }, [showAiPanel]);

    useEffect(() => {
        try {
            const config = getMorphoService(serviceNames.IConfigService);
            if (config) {
                const preValue = config.state.commentConfig.getValue();
                if (renderTarget === RenderTarget.AiAssistant) {
                    config.state.commentConfig.next({
                        ...preValue,
                        customWidth: showChat ? RightSidebarWidth : undefined,
                    });
                }
            }
        } catch (err) {
            console.error(err);
        }
    }, [renderTarget, showChat]);

    return showChat ? <PanelWrap
        curDocument={curDocument}
        renderTarget={renderTarget}
        continueChatData={continueChatData}
    /> : null;
};

export default store.connect([
    store.KEYS.CUR_DOCUMENT,
    store.KEYS.AI_CHAT_PANEL,
    store.KEYS.DOCUMENT_MODE,
])(AIChat);
